import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const isNumericSummaryValue = (value) => (
  value !== null && value !== undefined && Number.isFinite(Number(value))
);

const buildPortfolioReturnSummary = (details, selectedPortfolio) => {
  if (selectedPortfolio !== 'all') {
    const detail = details[0];
    return {
      totalValue: detail?.total_value ?? null,
      totalCost: detail?.total_cost ?? null,
      gainLoss: detail?.portfolio_gain_loss ?? null,
      gainLossPercent: detail?.portfolio_gain_loss_percent ?? null,
    };
  }

  const totalValueValues = details
    .map((detail) => detail?.total_value)
    .filter(isNumericSummaryValue)
    .map(Number);
  const totalCostValues = details
    .map((detail) => detail?.total_cost)
    .filter(isNumericSummaryValue)
    .map(Number);
  const totalValue = totalValueValues.length
    ? totalValueValues.reduce((sum, value) => sum + value, 0)
    : null;
  const totalCost = totalCostValues.length
    ? totalCostValues.reduce((sum, value) => sum + value, 0)
    : null;
  const gainLoss = isNumericSummaryValue(totalValue) && isNumericSummaryValue(totalCost) && totalCost > 0
    ? totalValue - totalCost
    : null;

  return {
    totalValue,
    totalCost,
    gainLoss,
    gainLossPercent: gainLoss !== null && totalCost > 0 ? (gainLoss / totalCost) * 100 : null,
  };
};

function Dashboard() {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const [dashboardAlerts, setDashboardAlerts] = useState([]);
  const [loadingDashboardAlerts, setLoadingDashboardAlerts] = useState(false);
  const [dashboardAlertsError, setDashboardAlertsError] = useState('');

  // Portfolio Return Tracking state
  const [selectedPortfolio, setSelectedPortfolio] = useState('all');
  const [selectedTimeRange, setSelectedTimeRange] = useState('6M');
  const [portfolioReturnData, setPortfolioReturnData] = useState(null);
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [loadingPortfolioReturn, setLoadingPortfolioReturn] = useState(true);
  const [portfolioReturnError, setPortfolioReturnError] = useState(null);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [loadingPortfolioSummary, setLoadingPortfolioSummary] = useState(false);
  const [portfolioHealth, setPortfolioHealth] = useState(null);
  const [loadingPortfolioHealth, setLoadingPortfolioHealth] = useState(true);
  const [portfolioHealthError, setPortfolioHealthError] = useState('');
  const [planUsage, setPlanUsage] = useState(null);
  const [loadingPlanUsage, setLoadingPlanUsage] = useState(true);
  const [planUsageError, setPlanUsageError] = useState('');
  const [topOpportunities, setTopOpportunities] = useState([]);
  const [loadingTopOpportunities, setLoadingTopOpportunities] = useState(true);
  const [topOpportunitiesError, setTopOpportunitiesError] = useState('');

   // Helper function for safe card navigation
   const isInteractiveTarget = (target) => {
    return target.closest('a, button, input, select, textarea');
  };

  const handleCardClick = (path, event) => {
    if (isInteractiveTarget(event.target)) {
      return;
    }

    navigate(path);
  };

  const handleCardKeyDown = (path, event) => {
    if (isInteractiveTarget(event.target)) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(path);
    }
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/me`, { method: 'GET' });

        if (!response.ok) {
          throw new Error('Failed to fetch user info');
        }

        const data = await response.json();
        setUserInfo(data);
      } catch (err) {
        console.error('Failed to fetch user info:', err);
      } finally {
        setLoadingUserInfo(false);
      }
    };

    fetchUserInfo();
  }, []);

  useEffect(() => {
    const fetchPortfolioReturnHistory = async () => {
      setLoadingPortfolioReturn(true);
      setPortfolioReturnError(null);

      try {
        const response = await apiFetch(
          `${API_BASE_URL}/dashboard/portfolio-return-history?portfolio_id=${selectedPortfolio}&period=${selectedTimeRange}`,
          {
            method: 'GET',
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch portfolio return history');
        }

        const data = await response.json();
        setPortfolioReturnData(data);
        setPortfolioOptions((currentOptions) => {
          const nextOptions = Array.isArray(data.portfolios) ? data.portfolios : [];
          const unchanged = currentOptions.length === nextOptions.length
            && currentOptions.every((option, index) => (
              option.id === nextOptions[index]?.id && option.name === nextOptions[index]?.name
            ));
          return unchanged ? currentOptions : nextOptions;
        });
      } catch (err) {
        console.error('Failed to fetch portfolio return history:', err);
        setPortfolioReturnError(err.message);
      } finally {
        setLoadingPortfolioReturn(false);
      }
    };

    fetchPortfolioReturnHistory();
  }, [selectedPortfolio, selectedTimeRange]);

  useEffect(() => {
    if (!portfolioOptions.length) {
      setPortfolioSummary(null);
      setLoadingPortfolioSummary(false);
      return undefined;
    }

    const targetPortfolios = selectedPortfolio === 'all'
      ? portfolioOptions
      : portfolioOptions.filter((portfolio) => String(portfolio.id) === String(selectedPortfolio));

    if (!targetPortfolios.length) {
      setPortfolioSummary(null);
      setLoadingPortfolioSummary(false);
      return undefined;
    }

    let ignore = false;
    const fetchPortfolioSummary = async () => {
      setLoadingPortfolioSummary(true);
      setPortfolioSummary(null);

      try {
        const details = await Promise.all(targetPortfolios.map(async (portfolio) => {
          const response = await apiFetch(`${API_BASE_URL}/portfolio/${portfolio.id}`, { method: 'GET' });
          if (!response.ok) {
            throw new Error('Failed to fetch portfolio summary');
          }
          return response.json();
        }));

        if (!ignore) {
          setPortfolioSummary(buildPortfolioReturnSummary(details, selectedPortfolio));
        }
      } catch (err) {
        console.error('Failed to fetch portfolio summary:', err);
        if (!ignore) {
          setPortfolioSummary(null);
        }
      } finally {
        if (!ignore) {
          setLoadingPortfolioSummary(false);
        }
      }
    };

    fetchPortfolioSummary();

    return () => {
      ignore = true;
    };
  }, [selectedPortfolio, portfolioOptions]);

  useEffect(() => {
    let ignore = false;

    const fetchPortfolioHealth = async () => {
      setLoadingPortfolioHealth(true);
      setPortfolioHealthError('');

      try {
        const response = await apiFetch(`${API_BASE_URL}/dashboard/portfolio-health`, { method: 'GET' });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const data = await response.json();
        if (!ignore) {
          setPortfolioHealth(data);
        }
      } catch (err) {
        console.error('Failed to fetch portfolio health:', err);
        if (!ignore) {
          setPortfolioHealth(null);
          setPortfolioHealthError(err.message || 'Failed to load portfolio health');
        }
      } finally {
        if (!ignore) {
          setLoadingPortfolioHealth(false);
        }
      }
    };

    fetchPortfolioHealth();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const fetchPlanUsage = async () => {
      setLoadingPlanUsage(true);
      setPlanUsageError('');

      try {
        const response = await apiFetch(`${API_BASE_URL}/dashboard/plan-usage`, { method: 'GET' });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const data = await response.json();
        if (!ignore) {
          setPlanUsage(data);
        }
      } catch (err) {
        console.error('Failed to fetch plan usage:', err);
        if (!ignore) {
          setPlanUsage(null);
          setPlanUsageError(err.message || 'Failed to load plan usage');
        }
      } finally {
        if (!ignore) {
          setLoadingPlanUsage(false);
        }
      }
    };

    fetchPlanUsage();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const fetchTopOpportunities = async () => {
      setLoadingTopOpportunities(true);
      setTopOpportunitiesError('');

      try {
        const scansResponse = await apiFetch(`${API_BASE_URL}/scans`, { method: 'GET' });

        if (!scansResponse.ok) {
          const errorData = await scansResponse.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const scansData = await scansResponse.json();
        const latestScan = Array.isArray(scansData.scans) ? scansData.scans[0] : null;

        if (!latestScan?.id) {
          if (!ignore) {
            setTopOpportunities([]);
          }
          return;
        }

        const detailResponse = await apiFetch(`${API_BASE_URL}/scans/${latestScan.id}`, { method: 'GET' });

        if (!detailResponse.ok) {
          const errorData = await detailResponse.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const detailData = await detailResponse.json();
        const results = Array.isArray(detailData.results) ? detailData.results : [];
        const sortedTopOpportunities = results
          .filter((result) => Number.isFinite(Number(result.rank)))
          .sort((a, b) => Number(a.rank) - Number(b.rank))
          .slice(0, 5);

        if (!ignore) {
          setTopOpportunities(sortedTopOpportunities);
        }
      } catch (err) {
        console.error('Failed to fetch top opportunities:', err);
        if (!ignore) {
          setTopOpportunities([]);
          setTopOpportunitiesError(err.message || 'Failed to load top opportunities');
        }
      } finally {
        if (!ignore) {
          setLoadingTopOpportunities(false);
        }
      }
    };

    fetchTopOpportunities();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const canFetchAlerts =
      userInfo && (userInfo.plan_type === 'pro' || userInfo.plan_type === 'unlimited');

    if (loadingUserInfo || !userInfo) {
      return;
    }

    if (!canFetchAlerts) {
      setDashboardAlerts([]);
      setDashboardAlertsError('');
      setLoadingDashboardAlerts(false);
      return;
    }

    let ignore = false;

    const fetchDashboardAlerts = async () => {
      setLoadingDashboardAlerts(true);
      setDashboardAlertsError('');

      try {
        const response = await apiFetch(`${API_BASE_URL}/alerts`, { method: 'GET' });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const data = await response.json();
        if (!ignore) {
          setDashboardAlerts(data.alerts || []);
        }
      } catch (err) {
        console.error('Failed to fetch dashboard alerts:', err);
        if (!ignore) {
          setDashboardAlerts([]);
          setDashboardAlertsError(err.message || 'Failed to load alerts');
        }
      } finally {
        if (!ignore) {
          setLoadingDashboardAlerts(false);
        }
      }
    };

    fetchDashboardAlerts();

    return () => {
      ignore = true;
    };
  }, [userInfo, loadingUserInfo]);

  const isPremium = userInfo && userInfo.plan_type === 'unlimited';
  const hasAlertsAccess = userInfo && (userInfo.plan_type === 'pro' || userInfo.plan_type === 'unlimited');

  const getEntryTimingBadgeClass = (entryTiming) => {
    const normalizedEntryTiming = (entryTiming || '').trim().toLowerCase();

    if (normalizedEntryTiming.includes('attractive entry')) {
      return 'dashboard-entry-attractive';
    }
    if (
      normalizedEntryTiming.includes('price elevated') ||
      normalizedEntryTiming.includes('extended') ||
      normalizedEntryTiming.includes('elevated')
    ) {
      return 'dashboard-entry-extended';
    }
    if (normalizedEntryTiming.includes('overbought')) {
      return 'dashboard-entry-overbought';
    }
    return '';
  };

  const activeDashboardAlerts = dashboardAlerts.filter((alert) => alert.status === 'ACTIVE');
  const dashboardAlertCounts = activeDashboardAlerts.reduce((counts, alert) => {
    const alertCategory = (alert.alert_type || alert.metric || '').toLowerCase();

    if (alertCategory === 'new_opportunity') {
      counts.newOpportunity += 1;
    } else if (alertCategory === 'low_investment_score') {
      counts.lowInvestmentScore += 1;
    } else if (alertCategory === 'review_position') {
      counts.reviewPosition += 1;
    } else if (alertCategory === 'missing_from_scan') {
      counts.missingFromScan += 1;
    }

    return counts;
  }, {
    newOpportunity: 0,
    lowInvestmentScore: 0,
    reviewPosition: 0,
    missingFromScan: 0,
  });
  const totalActiveDashboardAlerts = activeDashboardAlerts.length;
  const avgHoldingScoreValue = Number.isFinite(Number(portfolioHealth?.avg_holding_score))
    ? Number(portfolioHealth.avg_holding_score)
    : null;
  const clampedAvgHoldingScore = avgHoldingScoreValue !== null
    ? Math.max(0, Math.min(100, avgHoldingScoreValue))
    : 0;
  const formatPortfolioHealthCount = (value) => {
    if (loadingPortfolioHealth || portfolioHealthError) {
      return '—';
    }

    return Number.isFinite(Number(value))
      ? Number(value).toLocaleString()
      : '0';
  };
  const portfolioHealthGaugeDisplay = loadingPortfolioHealth || portfolioHealthError
    ? '—'
    : avgHoldingScoreValue !== null
      ? clampedAvgHoldingScore.toFixed(1)
      : '—';
  const planUsageItems = Array.isArray(planUsage?.items) ? planUsage.items : [
    { key: 'portfolios', label: 'Portfolios', used: null, limit: null, percent_used: 0, is_unlimited: false },
    { key: 'saved_scans', label: 'Saved Scans', used: null, limit: null, percent_used: 0, is_unlimited: false },
  ];
  const formatPlanUsageCount = (item) => {
    if (loadingPlanUsage || planUsageError) {
      return '— / —';
    }

    const used = Number.isFinite(Number(item?.used)) ? Number(item.used).toLocaleString() : '0';
    if (item?.is_unlimited) {
      return `${used} / Unlimited`;
    }

    const limit = Number.isFinite(Number(item?.limit)) ? Number(item.limit).toLocaleString() : '0';
    return `${used} / ${limit}`;
  };
  const getPlanUsageWidth = (item) => {
    if (loadingPlanUsage || planUsageError) {
      return '0%';
    }

    if (item?.is_unlimited) {
      return Number(item?.used) > 0 ? '36%' : '0%';
    }

    const percentUsed = Number.isFinite(Number(item?.percent_used))
      ? Math.max(0, Math.min(100, Number(item.percent_used)))
      : 0;
    return `${percentUsed}%`;
  };

  const formatSummaryCurrency = (value) => (
    isNumericSummaryValue(value)
      ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '—'
  );
  const formatSummaryGainLoss = () => {
    if (!isNumericSummaryValue(portfolioSummary?.gainLoss)) {
      return '—';
    }

    const dollars = formatSummaryCurrency(portfolioSummary.gainLoss);
    if (!isNumericSummaryValue(portfolioSummary.gainLossPercent)) {
      return dollars;
    }

    const percent = Number(portfolioSummary.gainLossPercent);
    return `${dollars} (${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%)`;
  };

  return (
    <div className="dashboard-grid dashboard-grid-viewport authenticated-page authenticated-page-content">
      {/* Left Column - Main Widgets */}
          <div className="dashboard-col-main">
            {/* Portfolio Return Tracking */}
            <div
              className="dashboard-widget dashboard-widget-chart dashboard-widget-clickable"
              onClick={(e) => handleCardClick('/portfolios', e)}
              onKeyDown={(e) => handleCardKeyDown('/portfolios', e)}
              tabIndex="0"
              role="button"
              aria-label="Navigate to Portfolios"
            >
              <div className="dashboard-widget-header">
                <h2 className="dashboard-widget-title dashboard-widget-title-with-icon">
                  <svg
                    className="dashboard-widget-title-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 17 9 11 13 15 21 7"></polyline>
                    <polyline points="15 7 21 7 21 13"></polyline>
                  </svg>
                  <span>Portfolio Return Tracking</span>
                </h2>
                <div className="dashboard-widget-controls">
                  <div
                    className="dashboard-widget-controls-container"
                    onClick={(e) => e.stopPropagation()}
                  >
                  <select
                    className="dashboard-select"
                    value={selectedPortfolio}
                    onChange={(e) => setSelectedPortfolio(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="all">All Portfolios</option>
                    {portfolioReturnData && portfolioReturnData.portfolios && portfolioReturnData.portfolios.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <div
                    className="dashboard-time-range"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {['1M', '3M', '6M', '1Y', 'ALL'].map(range => (
                      <button
                        key={range}
                        className={`dashboard-time-btn ${selectedTimeRange === range ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedTimeRange(range);
                        }}
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
              <div className="dashboard-chart-legend">
                <div className="dashboard-legend-item">
                  <span className="dashboard-legend-line dashboard-legend-line-portfolio"></span>
                  <span className="dashboard-legend-label">Portfolio Return %</span>
                </div>
                <div className="dashboard-legend-item">
                  <span className="dashboard-legend-line dashboard-legend-line-benchmark"></span>
                  <span className="dashboard-legend-label">SPY</span>
                </div>
              </div>
              <div className="dashboard-chart-container">
              <div
                className="dashboard-portfolio-return-summary"
                aria-label="Portfolio return summary"
                style={{ display: 'flex', justifyContent: 'space-around', gap: '1rem', padding: '0.75rem 0', flexWrap: 'wrap' }}
              >
                <div>
                  <div className="theme-text-muted">Total Value</div>
                  <strong className="theme-score">{loadingPortfolioSummary ? '—' : formatSummaryCurrency(portfolioSummary?.totalValue)}</strong>
                </div>
                <div>
                  <div className="theme-text-muted">Total Cost</div>
                  <strong className="theme-score">{loadingPortfolioSummary ? '—' : formatSummaryCurrency(portfolioSummary?.totalCost)}</strong>
                </div>
                <div>
                  <div className="theme-text-muted">Gain/Loss</div>
                  <strong className={isNumericSummaryValue(portfolioSummary?.gainLoss) ? (portfolioSummary.gainLoss >= 0 ? 'theme-success' : 'theme-error') : 'theme-text-muted'}>
                    {loadingPortfolioSummary ? '—' : formatSummaryGainLoss()}
                  </strong>
                </div>
              </div>
                {loadingPortfolioReturn ? (
                  <div className="dashboard-chart-state theme-text" style={{ textAlign: 'center', padding: '3rem' }}>
                    Loading portfolio data...
                  </div>
                ) : portfolioReturnError ? (
                  <div className="dashboard-chart-state theme-error" style={{ textAlign: 'center', padding: '3rem' }}>
                    Error loading data: {portfolioReturnError}
                  </div>
                ) : !portfolioReturnData || !portfolioReturnData.series || portfolioReturnData.series.length === 0 ? (
                  <div className="dashboard-chart-state theme-text" style={{ textAlign: 'center', padding: '3rem' }}>
                    {portfolioReturnData && portfolioReturnData.message ? portfolioReturnData.message : 'No data available'}
                  </div>
                ) : portfolioReturnData.series.length === 1 ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <div className="theme-text-muted" style={{ marginTop: '1rem' }}>
                      Not enough history yet to show return chart
                    </div>
                    <div className="theme-text-muted" style={{ marginTop: '0.5rem' }}>
                      The return line usually appears after roughly 1–2 months, once enough portfolio history is available.
                    </div>
                  </div>
                ) : (
                  <svg className="dashboard-chart-svg" viewBox="0 0 1000 230" preserveAspectRatio="none">
                    {/* Y-axis */}
                    <line x1="60" y1="20" x2="60" y2="200" className="dashboard-chart-axis-line" stroke="#475569" strokeWidth="1" />

                    {/* X-axis */}
                    <line x1="60" y1="200" x2="980" y2="200" className="dashboard-chart-axis-line" stroke="#475569" strokeWidth="1" />

                    {/* Horizontal grid lines */}
                    <line x1="60" y1="20" x2="980" y2="20" className="dashboard-chart-grid-line" stroke="rgba(71, 85, 105, 0.2)" strokeWidth="0.5" />
                    <line x1="60" y1="50" x2="980" y2="50" className="dashboard-chart-grid-line" stroke="rgba(71, 85, 105, 0.2)" strokeWidth="0.5" />
                    <line x1="60" y1="80" x2="980" y2="80" className="dashboard-chart-grid-line" stroke="rgba(71, 85, 105, 0.2)" strokeWidth="0.5" />
                    <line x1="60" y1="110" x2="980" y2="110" className="dashboard-chart-grid-line" stroke="rgba(71, 85, 105, 0.2)" strokeWidth="0.5" />
                    <line x1="60" y1="140" x2="980" y2="140" className="dashboard-chart-grid-line" stroke="rgba(71, 85, 105, 0.2)" strokeWidth="0.5" />
                    <line x1="60" y1="170" x2="980" y2="170" className="dashboard-chart-grid-line" stroke="rgba(71, 85, 105, 0.2)" strokeWidth="0.5" />
                    <line x1="60" y1="200" x2="980" y2="200" className="dashboard-chart-grid-line" stroke="rgba(71, 85, 105, 0.2)" strokeWidth="0.5" />

                    {/* Calculate dynamic Y-axis range */}
                    {(() => {
                      const series = portfolioReturnData.series;
                      const allReturns = series.flatMap(p => [
                        p.portfolio_return_percent,
                        p.spy_return_percent
                      ].filter(v => v !== null && v !== undefined));

                      const minReturn = Math.min(...allReturns);
                      const maxReturn = Math.max(...allReturns);
                      const padding = 0.1; // 10% padding

                      const yMin = Math.min(minReturn * (1 + padding), 0);
                      const yMax = Math.max(maxReturn * (1 + padding), 0);
                      const yRange = yMax - yMin;

                      // Generate Y-axis labels dynamically
                      const yTicks = [];
                      const tickCount = 6;
                      const tickStep = yRange / (tickCount - 1);

                      for (let i = 0; i < tickCount; i++) {
                        const value = yMin + (i * tickStep);
                        const yPos = 200 - ((value - yMin) / yRange * 180);
                        yTicks.push({ value, yPos });
                      }

                      return (
                        <>
                          {/* Y-axis labels */}
                          {yTicks.map((tick, i) => (
                            <text
                              key={i}
                              x="50"
                              y={tick.yPos}
                              className="dashboard-chart-tick-label"
                              textAnchor="end"
                              fill="var(--color-text-secondary)"
                              fontSize="10"
                            >
                              {tick.value.toFixed(1)}%
                            </text>
                          ))}

                          {/* X-axis labels - show one label per month */}
                          {(() => {
                            const monthLabels = [];
                            const monthMap = new Map();

                            series.forEach((point, i) => {
                              const date = new Date(point.date);
                              const monthKey = `${date.getFullYear()}-${date.getMonth()}`;

                              if (!monthMap.has(monthKey)) {
                                monthMap.set(monthKey, true);
                                const xPos = 60 + (i / (series.length - 1) * 920);
                                const label = selectedTimeRange === '1M'
                                  ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                  : date.toLocaleDateString('en-US', { month: 'short', year: selectedTimeRange === 'ALL' ? 'numeric' : undefined });

                                monthLabels.push({ xPos, label });
                              }
                            });

                            return monthLabels.map((label, i) => (
                              <text
                                key={i}
                                x={label.xPos}
                                y="220"
                                className="dashboard-chart-tick-label"
                                textAnchor="middle"
                                fill="var(--color-text-secondary)"
                                fontSize="10"
                              >
                                {label.label}
                              </text>
                            ));
                          })()}
                        </>
                      );
                    })()}

                    {/* Shared dynamic scale calculation for both lines */}
                    {(() => {
                      if (!portfolioReturnData?.series?.length) return null;

                      const series = portfolioReturnData.series;
                      const chartLeft = 60;
                      const chartTop = 20;
                      const chartWidth = 920;
                      const chartHeight = 180;

                      // Collect all return values (portfolio and SPY) that are valid numbers
                      const allReturns = series.flatMap(p => [
                        p.portfolio_return_percent,
                        p.spy_return_percent
                      ].filter(v => typeof v === 'number'));

                      if (allReturns.length === 0) return null;

                      // Calculate min/max with padding and ensure 0 is included
                      const minReturn = Math.min(0, ...allReturns);
                      const maxReturn = Math.max(0, ...allReturns);
                      const padding = 0.1 * (maxReturn - minReturn); // 10% padding
                      const yMin = minReturn - padding;
                      const yMax = maxReturn + padding;
                      const yRange = yMax - yMin || 1; // Avoid division by zero if all values equal

                      // Helper to convert return % to chart Y position
                      const getYPosition = (returnPercent) => {
                        if (returnPercent === null || returnPercent === undefined) return null;
                        return chartTop + chartHeight - chartHeight * ((returnPercent - yMin) / yRange);
                      };

                      // Render SPY line (gold) first so portfolio stays on top
                      const spyPath = series.map((p, i) => {
                        const y = getYPosition(p.spy_return_percent);
                        if (y === null) return null;
                        const x = chartLeft + (i / (series.length - 1) * chartWidth);
                        return i === 0 ? `M ${x},${y}` : `L ${x},${y}`;
                      }).filter(v => v !== null).join(' ');

                      // Then render portfolio line (green) on top
                      const portfolioPath = series.map((p, i) => {
                        const y = getYPosition(p.portfolio_return_percent);
                        const x = chartLeft + (i / (series.length - 1) * chartWidth);
                        return i === 0 ? `M ${x},${y}` : `L ${x},${y}`;
                      }).join(' ');

                      return (
                        <>
                          {spyPath && (
                            <path
                              d={spyPath}
                              fill="none"
                              stroke="#f5c542"
                              strokeWidth="2"
                              strokeLinejoin="round"
                            />
                          )}
                          <path
                            d={portfolioPath}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2.5"
                            strokeLinejoin="round"
                          />

                          {/* Add end-of-line percentage labels with value-based ordering */}
                          {(() => {
                            if (!portfolioReturnData?.series?.length) return null;

                            // Get latest valid points
                            const latestPortfolioPoint = portfolioReturnData.series[portfolioReturnData.series.length - 1];
                            const latestSpyPoint = portfolioReturnData.series.findLast(p => p.spy_return_percent !== null);

                            // Calculate positions
                            const chartRight = 980;
                            const labelX = chartRight - 15; // Anchor slightly left of edge
                            const labelOffset = 12;
                            const minLabelGap = 24; // Keep close labels readable while staying above both lines

                            // Get return values and Y positions
                            const portfolioReturn = latestPortfolioPoint?.portfolio_return_percent;
                            const spyReturn = latestSpyPoint?.spy_return_percent;
                            const portfolioY = portfolioReturn !== null ? getYPosition(portfolioReturn) : null;
                            const spyY = spyReturn !== null ? getYPosition(spyReturn) : null;

                            // Determine label positions based on return values
                            let topLabel = null;
                            let bottomLabel = null;

                            if (portfolioY !== null && spyY !== null) {
                              let portfolioLabelY = portfolioY - labelOffset;
                              let spyLabelY = spyY - labelOffset;
                              const isPortfolioHigher = portfolioY < spyY;

                              if (Math.abs(portfolioLabelY - spyLabelY) < minLabelGap) {
                                if (isPortfolioHigher) {
                                  portfolioLabelY = spyLabelY - minLabelGap;
                                } else {
                                  spyLabelY = portfolioLabelY - minLabelGap;
                                }
                              }

                              if (isPortfolioHigher) {
                                topLabel = { y: portfolioLabelY, value: portfolioReturn, color: 'var(--color-accent-primary)' };
                                bottomLabel = { y: spyLabelY, value: spyReturn, color: 'var(--color-accent-secondary)' };
                              } else {
                                topLabel = { y: spyLabelY, value: spyReturn, color: 'var(--color-accent-secondary)' };
                                bottomLabel = { y: portfolioLabelY, value: portfolioReturn, color: 'var(--color-accent-primary)' };
                              }
                            } else if (portfolioY !== null) {
                              topLabel = { y: portfolioY - labelOffset, value: portfolioReturn, color: 'var(--color-accent-primary)' };
                            } else if (spyY !== null) {
                              topLabel = { y: spyY - labelOffset, value: spyReturn, color: 'var(--color-accent-secondary)' };
                            }

                            return (
                              <>
                                {topLabel && (
                                  <text
                                    x={labelX}
                                    y={topLabel.y}
                                    textAnchor="end"
                                    fill={topLabel.color}
                                    fontSize="16"
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {topLabel.value.toFixed(1)}%
                                  </text>
                                )}
                                {bottomLabel && (
                                  <text
                                    x={labelX}
                                    y={bottomLabel.y}
                                    textAnchor="end"
                                    fill={bottomLabel.color}
                                    fontSize="16"
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {bottomLabel.value.toFixed(1)}%
                                  </text>
                                )}
                              </>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </svg>
                )}
              </div>

            </div>

            {/* Portfolio Health */}
            <div
              className="dashboard-widget dashboard-widget-health dashboard-widget-clickable"
              onClick={(e) => handleCardClick('/portfolios', e)}
              onKeyDown={(e) => handleCardKeyDown('/portfolios', e)}
              tabIndex="0"
              role="button"
              aria-label="Navigate to Portfolios"
            >
              <div className="dashboard-health-header">
                <svg
                  className="dashboard-health-title-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
                <h2 className="dashboard-widget-title">Portfolio Health</h2>
              </div>
              <div className="dashboard-health-content">
                <div className="dashboard-health-stat">
                  <div className="dashboard-health-value">{formatPortfolioHealthCount(portfolioHealth?.portfolio_count)}</div>
                  <div className="dashboard-health-label">Portfolios</div>
                </div>

                <div className="dashboard-health-divider"></div>

                <div className="dashboard-health-stat">
                  <div className="dashboard-health-value">{formatPortfolioHealthCount(portfolioHealth?.total_holdings)}</div>
                  <div className="dashboard-health-label">Total Holdings</div>
                </div>

                <div className="dashboard-health-divider"></div>

                <div className="dashboard-health-stat">
                  <div className="dashboard-health-value dashboard-health-value-danger">{formatPortfolioHealthCount(portfolioHealth?.holdings_with_alerts)}</div>
                  <div className="dashboard-health-label">Holdings w/ Alerts</div>
                </div>

                <div className="dashboard-health-divider"></div>

                <div className="dashboard-health-score-gauge">
                  <svg className="dashboard-health-gauge-svg" viewBox="0 0 120 70">
                    {/* Background arc */}
                    <path
                      d="M 20 60 A 40 40 0 0 1 100 60"
                      fill="none"
                      stroke="rgba(71, 85, 105, 0.3)"
                      strokeWidth="8"
                      strokeLinecap="round"
                    />
                    {/* Foreground arc - 68% of 180 degrees = ~122 degrees */}
                    <path
                      d="M 20 60 A 40 40 0 0 1 100 60"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="8"
                      strokeLinecap="round"
                      pathLength="100"
                      strokeDasharray={`${clampedAvgHoldingScore} 100`}
                    />
                  </svg>
                  <div className="dashboard-health-gauge-value">{portfolioHealthGaugeDisplay}</div>
                  <div className="dashboard-health-gauge-label">
                    Avg. Holding Score
                    {!loadingPortfolioHealth && portfolioHealthError && (
                      <span className="theme-warning" style={{ display: 'block', marginTop: '0.2rem' }}>
                        Unavailable
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Opportunities */}
            <div className="dashboard-widget">
              <h2 className="dashboard-widget-title">Top Opportunities (From Latest Scan)</h2>
              <div className="dashboard-table-wrapper">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Ticker</th>
                      <th>Company</th>
                      <th>Investment Score</th>
                      <th>Entry Timing</th>
                      <th>View Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingTopOpportunities && (
                      <tr>
                        <td colSpan="6">Loading latest scan opportunities...</td>
                      </tr>
                    )}

                    {!loadingTopOpportunities && topOpportunitiesError && (
                      <tr>
                        <td colSpan="6">Unable to load latest scan opportunities.</td>
                      </tr>
                    )}

                    {!loadingTopOpportunities && !topOpportunitiesError && topOpportunities.length === 0 && (
                      <tr>
                        <td colSpan="6">No scan results available yet.</td>
                      </tr>
                    )}

                    {!loadingTopOpportunities && !topOpportunitiesError && topOpportunities.length > 0 && (
                      topOpportunities.map((opportunity, index) => {
                        const entryTimingBadgeClass = getEntryTimingBadgeClass(opportunity.entry_timing);
                        const investmentScore = Number(opportunity.investment_score);
                        const hasNumericInvestmentScore = Number.isFinite(investmentScore);

                        return (
                          <tr key={`${opportunity.ticker || 'scan-opportunity'}-${opportunity.rank || index}`}>
                            <td>{Number.isFinite(Number(opportunity.rank)) ? Number(opportunity.rank) : '—'}</td>
                            <td className="dashboard-ticker">{opportunity.ticker || '—'}</td>
                            <td>{opportunity.company_name || '—'}</td>
                            <td className={hasNumericInvestmentScore ? 'dashboard-score-high' : ''}>
                              {hasNumericInvestmentScore ? investmentScore.toFixed(1) : '—'}
                            </td>
                            <td>
                              {opportunity.entry_timing ? (
                                <span className={`dashboard-entry-timing-badge ${entryTimingBadgeClass}`.trim()}>
                                  {opportunity.entry_timing}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              {opportunity.ticker ? (
                                <Link to={`/stock/${opportunity.ticker}`} className="dashboard-link">View →</Link>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column - Side Widgets */}
          <div className="dashboard-col-side">
            {/* Alerts */}
            <div
              className="dashboard-widget dashboard-widget-alerts dashboard-widget-clickable"
              onClick={(e) => handleCardClick('/alerts', e)}
              onKeyDown={(e) => handleCardKeyDown('/alerts', e)}
              tabIndex="0"
              role="button"
              aria-label="Navigate to Alerts"
            >
              <h2 className="dashboard-widget-title dashboard-widget-title-with-icon">
                <svg
                  className="dashboard-widget-title-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                <span>Alerts</span>
              </h2>
              <div className="dashboard-alert-list">
                {loadingUserInfo || (hasAlertsAccess && loadingDashboardAlerts) ? (
                  <div className="dashboard-alert-item">
                    <span className="dashboard-alert-label">Loading alerts...</span>
                  </div>
                ) : !hasAlertsAccess ? (
                  <>
                    <div className="dashboard-alert-item">
                      <span className="dashboard-alert-left">
                        <svg className="dashboard-alert-icon dashboard-alert-icon-warning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="10" rx="2"></rect>
                          <path d="M7 11V8a5 5 0 0 1 10 0v3"></path>
                        </svg>
                        <span className="dashboard-alert-label">Pro / Premium feature</span>
                      </span>
                      <span className="dashboard-alert-count dashboard-alert-count-warning">Locked</span>
                    </div>
                    <div className="dashboard-alert-item dashboard-alert-item-total">
                      <span className="dashboard-alert-label">Upgrade to access portfolio alerts</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="dashboard-alert-item">
                      <span className="dashboard-alert-left">
                        <svg className="dashboard-alert-icon dashboard-alert-icon-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="7" width="18" height="13" rx="2"></rect>
                          <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <path d="M12 11v5"></path>
                          <path d="M9.5 13.5h5"></path>
                        </svg>
                        <span className="dashboard-alert-label">New Opportunities</span>
                      </span>
                      <span className="dashboard-alert-right">
                        <span className="dashboard-alert-count dashboard-alert-count-success">{dashboardAlertCounts.newOpportunity}</span>
                        <svg className="dashboard-alert-trend-icon dashboard-alert-icon-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 17 9 11 13 15 21 7"></polyline>
                          <polyline points="15 7 21 7 21 13"></polyline>
                        </svg>
                      </span>
                    </div>

                    <div className="dashboard-alert-item">
                      <span className="dashboard-alert-left">
                        <svg className="dashboard-alert-icon dashboard-alert-icon-warning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="9"></circle>
                          <path d="M4 20L20 4"></path>
                        </svg>
                        <span className="dashboard-alert-label">Low Score Alerts</span>
                      </span>
                      <span className="dashboard-alert-right">
                        <span className="dashboard-alert-count dashboard-alert-count-danger">{dashboardAlertCounts.lowInvestmentScore}</span>
                        <svg className="dashboard-alert-trend-icon dashboard-alert-icon-warning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <polyline points="19 12 12 19 5 12"></polyline>
                        </svg>
                      </span>
                    </div>

                    <div className="dashboard-alert-item">
                      <span className="dashboard-alert-left">
                        <svg className="dashboard-alert-icon dashboard-alert-icon-warning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="8" y1="6" x2="21" y2="6"></line>
                          <line x1="8" y1="12" x2="21" y2="12"></line>
                          <line x1="8" y1="18" x2="21" y2="18"></line>
                          <circle cx="4" cy="6" r="1"></circle>
                          <circle cx="4" cy="12" r="1"></circle>
                          <circle cx="4" cy="18" r="1"></circle>
                        </svg>
                        <span className="dashboard-alert-label">Outside Preferred Rank</span>
                      </span>
                      <span className="dashboard-alert-count dashboard-alert-count-warning">{dashboardAlertCounts.reviewPosition}</span>
                    </div>

                    <div className="dashboard-alert-item">
                      <span className="dashboard-alert-left">
                        <svg className="dashboard-alert-icon dashboard-alert-icon-danger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="9"></circle>
                          <path d="M8 12h8"></path>
                          <path d="M9 9l6 6"></path>
                          <path d="M15 9l-6 6"></path>
                        </svg>
                        <span className="dashboard-alert-label">Outside Current Universe</span>
                      </span>
                      <span className="dashboard-alert-count dashboard-alert-count-warning">{dashboardAlertCounts.missingFromScan}</span>
                    </div>
                    <div className="dashboard-alert-item dashboard-alert-item-total">
                      <span className="dashboard-alert-label">
                        Total Active Alerts
                        {totalActiveDashboardAlerts === 0 && (
                          <span className="theme-text-muted" style={{ display: 'block', marginTop: '0.25rem' }}>
                            All caught up
                          </span>
                        )}
                        {dashboardAlertsError && (
                          <span className="theme-warning theme-caption" style={{ display: 'block', marginTop: '0.25rem' }}>
                            {dashboardAlertsError}
                          </span>
                        )}
                      </span>
                      <span className="dashboard-alert-count">{totalActiveDashboardAlerts}</span>
                    </div>
                  </>
                )}
              </div>

            </div>

            {/* Strategy Lab Snapshot */}
            <div
              className="dashboard-widget dashboard-widget-premium dashboard-widget-clickable dashboard-strategy-lab-widget"
              onClick={(e) => handleCardClick('/validation-lab', e)}
              onKeyDown={(e) => handleCardKeyDown('/validation-lab', e)}
              tabIndex="0"
              role="button"
              aria-label="Navigate to Strategy Lab"
            >
              <div className="dashboard-widget-header-row">
                <h2 className="dashboard-widget-title">Strategy Lab</h2>

              </div>

              <p className="dashboard-validation-note">
                Historical strategy evidence and benchmark performance for the current model.
              </p>

              <div className={`dashboard-validation-preview ${!isPremium ? 'dashboard-validation-blurred' : ''}`}>
                <div className="dashboard-table-wrapper">
                  <table className="dashboard-table dashboard-table-compact dashboard-strategy-lab-table">
                    <thead>
                      <tr>
                        <th>Mode</th>
                        <th>Best<br />Top N</th>
                        <th>Compounded<br />Return</th>
                        <th>vs SPY<br />Benchmark</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Balanced <span className="dashboard-strategy-lab-mode-detail">(Equal Weight)</span></td>
                        <td>Top 25</td>
                        <td className="dashboard-score-high">+18.2%</td>
                        <td className="dashboard-score-high">+4.7%</td>
                      </tr>
                      <tr>
                        <td>Growth <span className="dashboard-strategy-lab-mode-detail">(Return Consistency)</span></td>
                        <td>Top 10</td>
                        <td className="dashboard-score-high">+21.5%</td>
                        <td className="dashboard-score-high">+8.0%</td>
                      </tr>
                      <tr>
                        <td>Conservative <span className="dashboard-strategy-lab-mode-detail">(Risk Stability)</span></td>
                        <td>Top 50</td>
                        <td className="dashboard-score-high">+15.8%</td>
                        <td className="dashboard-score-high">+2.3%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {isPremium ? (
                <Link to="/validation-lab" className="dashboard-btn-upgrade">
                  View Strategy Lab
                </Link>
              ) : (
                <>
                  <Link to="/pricing" className="dashboard-btn-upgrade">
                    Upgrade to Premium
                  </Link>
                </>
              )}

            </div>

            {/* Plan & Usage */}
            <div
              className="dashboard-widget dashboard-widget-clickable dashboard-widget-plan-usage"
              onClick={(e) => handleCardClick('/billing', e)}
              onKeyDown={(e) => handleCardKeyDown('/billing', e)}
              tabIndex="0"
              role="button"
              aria-label="Navigate to Billing"
            >
              <h2 className="dashboard-widget-title">Plan & Usage</h2>
              <div className="dashboard-usage-list">
                {planUsageItems.map((item) => (
                  <div key={item.key} className="dashboard-usage-item">
                    <div className="dashboard-usage-header">
                      <span className="dashboard-usage-label">{item.label}</span>
                      <span className="dashboard-usage-count">{formatPlanUsageCount(item)}</span>
                    </div>
                    <div className="dashboard-usage-bar">
                      <div
                        className="dashboard-usage-fill"
                        style={{ width: getPlanUsageWidth(item) }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
              {!loadingPlanUsage && planUsageError && (
                <div className="theme-text-muted" style={{ marginTop: '0.4rem' }}>
                  Usage unavailable
                </div>
              )}

            </div>
          </div>
        </div>
  );
}

export default Dashboard;
